import { Grid } from "@mui/material";
import React, { useEffect, useState } from "react";
import QTDetailsTextFieldWithLabelSection from "../../../../../common/QTDetailsTextFieldWithLabelSection";
import { toTitleCase } from "../../../../../utils/stringUtils";
import QTDetailsAutoCompleteWithLabelSection from "../../../../../common/QTDetailsAutoCompleteWithLabelSection";
import { getSingleUser, getSingleAdmin } from "../../../../../modules/OrganizationQuickTales/hooks/QuickTalesHooks";
import moment from "moment";

const QTDetailsOrganizationSection = ({
  quicktaleDetails,
  setQuicktaleDetails,
  editEnabled,
  organizationsData,
  organizationName,
  currentUser,
}) => {
  const [initialOrgId, setInitialOrgId] = useState(quicktaleDetails?.organization);
  const [initialStatus, setInitialStatus] = useState(quicktaleDetails?.approvalStatus?.status);
  const [updatedByName, setUpdatedByName] = useState("");
  const [resolvedOrganizationName, setResolvedOrganizationName] = useState(organizationName || "");

  // Fetch organization name based on ID if not provided
  useEffect(() => {
    const resolveOrganizationName = async () => {
      console.log("Resolving organization name for ID:", quicktaleDetails?.organization);
      if (quicktaleDetails?.organization && organizationsData.length > 0) {
        const org = organizationsData.find(o => o.id === quicktaleDetails.organization);
        console.log("Found organization:", org.name);
        if (org && org.name) {
          console.log("Setting resolved organization name to:", org.name);
          setResolvedOrganizationName(org.name);
        } else {
          console.log("Organization ID not found in organizationsData");
          setResolvedOrganizationName("");
        }
      } else if (quicktaleDetails?.organization && organizationsData.length === 0) {
        // If no organizations data yet, keep the provided organizationName or show empty
        console.log("No organizations data available, using provided organizationName:", organizationName);
        setResolvedOrganizationName(organizationName || "");
      } else {
        console.log("No organization ID in quicktaleDetails");
        setResolvedOrganizationName("");
      }
    };

    resolveOrganizationName();
  }, [quicktaleDetails?.organization, organizationsData, organizationName]);

  // Fetch user/admin name if updatedBy is an ID
  useEffect(() => {
    const fetchUpdatedByName = async () => {
      if (quicktaleDetails?.approvalStatus?.updatedBy) {
        const updatedBy = quicktaleDetails.approvalStatus.updatedBy;

        // Check if it's plain text (contains a space) - display as-is
        if (updatedBy.includes(' ')) {
          setUpdatedByName(updatedBy);
        } else {
          // Try to fetch as a regular user first
          try {
            const userData = await getSingleUser(updatedBy);
            if (userData?.data()) {
              const user = userData.data();
              setUpdatedByName(`${user.firstName || ""} ${user.lastName || ""}`.trim());
              return;
            }
          } catch (error) {
            console.log("User not found, trying as admin...");
          }

          // If not found as user, try to fetch as an admin
          try {
            const adminData = await getSingleAdmin(updatedBy);
            if (adminData?.data()) {
              const admin = adminData.data();
              setUpdatedByName(`${admin.firstName || ""} ${admin.lastName || ""}`.trim());
              return;
            }
          } catch (error) {
            console.log("Admin not found either...");
          }

          // If both user and admin lookups fail, display the value as-is
          setUpdatedByName(updatedBy);
        }
      } else {
        setUpdatedByName("");
      }
    };

    fetchUpdatedByName();
  }, [quicktaleDetails?.approvalStatus?.updatedBy]);

  // Track when org or status changes to auto-update updatedAt and updatedBy
  useEffect(() => {
    if (!editEnabled) {
      // Reset initial values when entering edit mode
      setInitialOrgId(quicktaleDetails?.organization);
      setInitialStatus(quicktaleDetails?.approvalStatus?.status);
    }
  }, [editEnabled, quicktaleDetails?.organization, quicktaleDetails?.approvalStatus?.status]);

  const handleOrganizationChange = (orgData) => {
    console.log("Organization change triggered with data:", orgData);
    const orgId = orgData?.id || null;

    // Check if organization actually changed
    if (orgId !== initialOrgId) {
      setQuicktaleDetails({
        ...quicktaleDetails,
        organization: orgId,
        approvalStatus: {
          ...quicktaleDetails.approvalStatus,
          status: "Pending",
          updatedAt: {
            seconds: Math.floor(Date.now() / 1000),
            nanoseconds: (Date.now() % 1000) * 1000000
          },
          updatedBy: currentUser.id, // Save admin ID, not name
        },
      });
    } else {
      // Just update organization without touching approvalStatus
      setQuicktaleDetails({
        ...quicktaleDetails,
        organization: orgId,
      });
    }
  };

  const handleStatusChange = (e) => {
    const newStatus = e.target.value;

    // Check if status actually changed
    if (newStatus !== initialStatus) {
      setQuicktaleDetails({
        ...quicktaleDetails,
        approvalStatus: {
          ...quicktaleDetails.approvalStatus,
          status: newStatus,
          updatedAt: {
            seconds: Math.floor(Date.now() / 1000),
            nanoseconds: (Date.now() % 1000) * 1000000
          },
          updatedBy: currentUser.id, // Save admin ID, not name
        },
      });
    } else {
      // Just update status without touching updatedAt/updatedBy
      setQuicktaleDetails({
        ...quicktaleDetails,
        approvalStatus: {
          ...quicktaleDetails.approvalStatus,
          status: newStatus,
        },
      });
    }
  };

  // Format the updated date for display
  const formattedUpdatedDate = quicktaleDetails?.approvalStatus?.updatedAt
    ? (() => {
        try {
          const dateToFormat = moment.unix(quicktaleDetails.approvalStatus.updatedAt.seconds).format("MM/DD/YYYY HH:mm:ss")
          return dateToFormat;
        } catch (error) {
          console.error("Error formatting date:", error);
          return "Invalid date";
        }
      })()
    : "";

  return (
    <Grid container style={organizationContainerStyles}>
      {/*left side column*/}
      <Grid item container direction={"column"} xs>
        {/*Organization section*/}
        <QTDetailsAutoCompleteWithLabelSection
          label={"Organization"}
          value={resolvedOrganizationName || ""}
          onChange={handleOrganizationChange}
          disabled={true}
          optionsData={organizationsData}
          editEnabled={editEnabled}
        />
        {/*Updated Date section*/}
        <QTDetailsTextFieldWithLabelSection
          label={"Updated Date"}
          value={formattedUpdatedDate}
          disabled={true}
        />
      </Grid>
      {/*right side column*/}
      <Grid item container direction={"column"} xs>
        {/*Status section*/}
        <QTDetailsTextFieldWithLabelSection
          label={"QT Org Status"}
          value={toTitleCase(quicktaleDetails?.approvalStatus?.status) || "Pending"}
          disabled={true}
          editEnabled={editEnabled}
          select={true}
          dropdownValues={["Approved", "Pending", "Denied"]}
          onChange={handleStatusChange}
        />
        {/*Updated By section*/}
        <QTDetailsTextFieldWithLabelSection
          label={"Updated By"}
          value={updatedByName || ""}
          disabled={true}
        />
      </Grid>
    </Grid>
  );
};

export default QTDetailsOrganizationSection;

const organizationContainerStyles = {
  borderRadius: 12,
  boxShadow: " 0px 0px 30px 0px rgba(80, 85, 136, 0.15)",
  background: "var(--gray-99, #FCFCFC)",
  padding: "36px 15px",
  marginTop: 20,
};
